"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchInput } from "@/fetch-ui/FetchInput";
import { FetchTextArea } from "@/fetch-ui/FetchTextArea";
import { useToast } from "@/components/providers/FetchToastProvider";
import { venuePartner } from "@/lib/database/client/database";
import { getCurrentUser } from "@/lib/db/users";
import { createClient } from "@/utils/supabase/client";
import { useEffect, useState } from "react";
import { EMPTY_PARTNER_FORM, PartnerFormValues, trimPartnerForm, validatePartnerForm } from "../_logic/partner-form";
import PartnerThankYou from "./PartnerThankYou";

const LABEL_CLASS = "text-sm font-semibold text-gray-700";

/** Required fields in order, with labels and input hints. Rendered top to bottom. */
const FIELDS: Array<{
  key: Exclude<keyof PartnerFormValues, "referralSource">;
  label: string;
  placeholder: string;
  type: "text" | "email" | "url";
  autoComplete: string;
}> = [
  { key: "fullName", label: "Your name", placeholder: "Jane Smith", type: "text", autoComplete: "name" },
  { key: "email", label: "Your email", placeholder: "jane@hotel.com", type: "email", autoComplete: "email" },
  {
    key: "propertyName",
    label: "Property name",
    placeholder: "The Grand Hotel",
    type: "text",
    autoComplete: "organization",
  },
  {
    key: "propertyWebsite",
    label: "Property website",
    placeholder: "grandhotel.com",
    type: "url",
    autoComplete: "url",
  },
];

/**
 * Venue partner sign-up form on the /partners page. Saves to `nowadays.venue_partners`
 * and swaps to a thank-you state on success.
 *
 * @returns The sign-up form, or the thank-you state after a successful submit.
 */
export default function PartnerForm() {
  // Hooks
  const { showToast } = useToast();

  // States
  const [supabase] = useState(() => createClient());
  const [values, setValues] = useState<PartnerFormValues>(EMPTY_PARTNER_FORM);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Derived values
  const errors = submitAttempted ? validatePartnerForm(values) : {};

  // Handlers
  const handleChange = (key: keyof PartnerFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitAttempted(true);
    if (Object.keys(validatePartnerForm(values)).length > 0) return;

    setSubmitting(true);
    try {
      await venuePartner(supabase, trimPartnerForm(values));
      setValues(EMPTY_PARTNER_FORM);
      setSubmitAttempted(false);
      setSubmitted(true);
    } catch (err) {
      console.error("PartnerForm.handleSubmit: sign-up failed", err);
      showToast("Submission failed. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Effects
  // Prefill name and email for signed-in users. Never overwrite what the venue already typed.
  useEffect(() => {
    (async () => {
      const user = await getCurrentUser(supabase);
      if (!user) return;
      setValues((prev) => ({
        ...prev,
        fullName: prev.fullName || `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim(),
        email: prev.email || user.email || "",
      }));
    })();
  }, [supabase]);

  // Render
  if (submitted) return <PartnerThankYou onSubmitAnother={() => setSubmitted(false)} />;

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
      {/* "Partner" is capitalized on purpose: for Nowadays it is a proper noun. */}
      <h2 className="text-h4 text-landing-primary">Become a Partner</h2>

      <div className="flex flex-col gap-4">
        {FIELDS.map((field) => (
          <div key={field.key} className="flex flex-col gap-1.5">
            <label htmlFor={`partner-${field.key}`} className={LABEL_CLASS}>
              {field.label}
              {/* Visual cue only. The input's `required` attribute tells screen readers. */}
              <span className="text-red-500" aria-hidden="true">
                *
              </span>
            </label>
            <FetchInput
              id={`partner-${field.key}`}
              name={field.key}
              type={field.type}
              autoComplete={field.autoComplete}
              placeholder={field.placeholder}
              value={values[field.key]}
              onChange={(e) => handleChange(field.key, e.target.value)}
              error={Boolean(errors[field.key])}
              helperText={errors[field.key]}
              required
            />
          </div>
        ))}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="partner-referralSource" className={LABEL_CLASS}>
            How did you hear about us?
          </label>
          <FetchTextArea
            id="partner-referralSource"
            name="referralSource"
            placeholder="A colleague, a Nowadays RFP, LinkedIn..."
            rows={3}
            value={values.referralSource}
            onChange={(e) => handleChange("referralSource", e.target.value)}
          />
        </div>
      </div>

      <FetchButton type="submit" variant="primary" color="primary" size="md" loading={submitting} className="w-full">
        Submit
      </FetchButton>
    </form>
  );
}
